import {
  OrderIntakeMaterialOptionPayload,
  OrderIntakeOption,
  OrderRequest,
  PersistedOrderRequest
} from '../../declarations/types'
import React from 'react'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {useNotification} from '@hconnect/uikit'
import {useTranslation} from 'react-i18next'
import {getValidMaterialOptions, isMaterialInvalidBySlots} from '../utils'
import {PersistedMaterialsAction} from '../../components/PersistedMaterialsDialog/PersistedMaterialsBanner'
import {trackEvent} from '../../../common/analytics'

interface UsePersistedMaterialsParams {
  selectedSite: OrderIntakeOption
  orders: OrderRequest[]
  defaultOrderRequest: OrderRequest
  isSlotsManagementEnabled: boolean
  useConfigurableSlots?: boolean
  slotConfiguration: any
  persistedOrderRequests: PersistedOrderRequest[] | undefined
  shippingType: ShippingType
  userId: string | undefined
  userProfileCountry: string | undefined
  userPrimaryOrganisationalUnit: string | undefined
  customerId: string | undefined
  siteNumber: string
  defaultBusinessLine: string | undefined
}

export const usePersistedMaterialsState = ({
  selectedSite,
  orders,
  defaultOrderRequest,
  isSlotsManagementEnabled,
  useConfigurableSlots,
  slotConfiguration,
  persistedOrderRequests,
  shippingType,
  userId,
  userProfileCountry,
  userPrimaryOrganisationalUnit,
  customerId,
  siteNumber,
  defaultBusinessLine
}: UsePersistedMaterialsParams) => {
  const {notify} = useNotification()
  const {t} = useTranslation()

  const [showBanner, setShowBanner] = React.useState(true)
  const [showDialog, setShowDialog] = React.useState(false)
  const [validMaterials, setValidMaterials] = React.useState<OrderIntakeMaterialOptionPayload[]>([])
  const [existsValidPersistedMaterials, setExistsValidPersistedMaterials] = React.useState(false)

  // Recompute valid materials whenever dependencies change
  const refreshValidMaterials = React.useCallback(() => {
    const validMaterialsOptions = getValidMaterialOptions(
      selectedSite,
      isSlotsManagementEnabled,
      orders,
      defaultOrderRequest,
      slotConfiguration,
      isMaterialInvalidBySlots,
      useConfigurableSlots
    )

    setValidMaterials(validMaterialsOptions)
  }, [
    selectedSite,
    isSlotsManagementEnabled,
    orders,
    defaultOrderRequest,
    slotConfiguration,
    useConfigurableSlots
  ])

  // check if any persisted materials are still valid whenever valid materials or persisted materials change
  React.useEffect(() => {
    const existsValid = persistedOrderRequests?.some((material) =>
      validMaterials.some(
        (item) => item.material.materialEnteredNumber === material.materialEnteredNumber
      )
    )

    setExistsValidPersistedMaterials(!!existsValid)
  }, [validMaterials, persistedOrderRequests])

  // Filter persisted materials to only include those that are still valid
  const getValidPersistedOrderRequests = () =>
    persistedOrderRequests?.filter((persistedItem) =>
      validMaterials.some(
        (item) => item.material.materialEnteredNumber === persistedItem.materialEnteredNumber
      )
    )

  const analyticsContext = {
    user_uuid: userId,
    country: userProfileCountry,
    primaryOrganisationalUnit: userPrimaryOrganisationalUnit,
    customerId: customerId,
    siteId: siteNumber,
    shippingType: shippingType,
    businessLine: defaultBusinessLine
  }

  const onCloseBanner = (action: PersistedMaterialsAction) => {
    switch (action) {
      case PersistedMaterialsAction.ApplyMaterials:
        trackEvent('hubOrderIntakeShortcutCopyPersistedMaterials', analyticsContext)
        notify('info', t('orderIntake.materialPersistency.confirmationMessage'), {
          autoHideDuration: 2000
        })
        return 'apply' as const
      case PersistedMaterialsAction.ShowMaterials:
        setShowDialog(true)
        return 'show' as const
      default:
        setShowBanner(false)
        return 'dismiss' as const
    }
  }

  const onCloseDialog = (apply: boolean) => {
    setShowDialog(false)
    return apply
  }

  const trackReviewPersistedMaterials = () => {
    trackEvent('hubOrderIntakeReviewPersistedMaterials', analyticsContext)
  }

  return {
    showBanner,
    showDialog,
    validMaterials,
    existsValidPersistedMaterials,
    refreshValidMaterials,
    getValidPersistedOrderRequests,
    onCloseBanner,
    onCloseDialog,
    trackReviewPersistedMaterials
  }
}
